// src/components/modals/DeleteConfirmation.jsx
import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  title,
  message,
  confirmText,
  cancelText
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  const resolvedTitle = title ?? t('pets.deleteModal.title')
  const resolvedMessage = message ?? t('pets.deleteModal.message')
  const resolvedConfirmText = confirmText ?? t('pets.deleteModal.confirm')
  const resolvedCancelText = cancelText ?? t('common.cancel')
  const deletingText = t('pets.deleteModal.deleting')

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose()
    }
  }

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isLoading])



  if (!isOpen) {
    return null
  }

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 0.2s ease-out',
          fontFamily: '"Red Hat Display", sans-serif',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px', textAlign: isRTL ? 'right' : 'center' }}>
          {/* Warning Icon */}
          <div
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#FEF2F2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
            </svg>
          </div>

          {/* Title */}
          <h3
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}
          >
            {resolvedTitle}
          </h3>

          {/* Message */}
          <p
            style={{
              fontSize: '16px',
              color: '#6B7280',
              margin: 0,
              lineHeight: '1.5'
            }}
          >
            {resolvedMessage}
          </p>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          {/* Cancel Button */}
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: isLoading ? '#9CA3AF' : '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#F9FAFB'
                e.target.style.borderColor = '#9CA3AF'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'transparent'
                e.target.style.borderColor = '#D1D5DB'
              }
            }}
          >
            {resolvedCancelText}
          </button>

          {/* Delete Button */}
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: isLoading ? '#FCA5A5' : '#EF4444',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#DC2626'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#EF4444'
              }
            }}
          >
            {isLoading && (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #FFFFFF',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            )}
            {isLoading ? deletingText : resolvedConfirmText}
          </button>
        </div>
      </div>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )

  // Render in portal to body
  return createPortal(modalContent, document.body)
}

export default DeleteConfirmationModal